import useResizeObserver from '@react-hook/resize-observer';
import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

export type CachedPDFContext = {
    _scaleDown: () => void;
    _scaleUp: () => void;
    _setCurrentPage: (page: number) => void;
    _setMaxPages: (pages: number) => void;
    _setRef: (ref: HTMLDivElement | null) => void;
    _setShowPagePreviews: (show: boolean) => void;
    currentPage: number;
    gotoPage: (page: number) => void;
    maxPages: number;
    scale: number;
    scrollRect: DOMRect;
    scrollRef: HTMLDivElement | null;
    showPagePreviews: boolean;
};
const Context = createContext<CachedPDFContext>({
    _scaleDown: () => {},
    _scaleUp: () => {},
    _setCurrentPage: () => {},
    _setMaxPages: () => {},
    _setRef: () => {},
    _setShowPagePreviews: () => {},
    currentPage: 0,
    gotoPage: () => {},
    maxPages: 0,
    scale: 1,
    scrollRect: new DOMRect(),
    scrollRef: null,
    showPagePreviews: false,
});

type CachedPDFContextProviderProps = {
    children: ReactNode;
};
export const CachedPDFContextProvider: FC<CachedPDFContextProviderProps> = ({
    children,
}) => {
    const [showPagePreviews, setShowPagePreviews] = useState(false);
    const [scrollRef, setScrollRef] = useState<HTMLDivElement | null>(null);
    const [scrollRect, setScrollRect] = useState(new DOMRect());
    useResizeObserver<HTMLDivElement>(scrollRef, (entry) =>
        setScrollRect(
            new DOMRect(
                entry.contentRect.x,
                entry.contentRect.y,
                entry.contentRect.width,
                entry.contentRect.width * 1.41,
            ),
        ),
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [maxPages, setMaxPages] = useState(1);
    const [scale, setScale] = useState(1);

    const handleScaleUp = useCallback(() => {
        setScale((prev) => Number((prev + 0.1).toFixed(2)));
    }, [scale]);
    const handleScaleDown = useCallback(() => {
        if (scale <= 1) return;
        setScale((prev) => Number((prev - 0.1).toFixed(2)));
    }, [scale]);

    const handleSetShowPagePreviews = useCallback(
        (show: boolean) => {
            setShowPagePreviews(show);
        },
        [showPagePreviews],
    );

    const value: CachedPDFContext = useMemo(
        () => ({
            _scaleDown: handleScaleDown,
            _scaleUp: handleScaleUp,
            _setCurrentPage: (page: number) => setCurrentPage(page),
            _setMaxPages: (pages: number) => setMaxPages(pages),
            _setRef: (ref: HTMLDivElement | null) => setScrollRef(ref),
            _setShowPagePreviews: handleSetShowPagePreviews,
            currentPage,
            gotoPage: (page: number) => {
                if (scrollRef) {
                    const element = scrollRef.querySelector(
                        `[data-page="${page}"]`,
                    );
                    if (!element) return;
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }
            },
            maxPages,
            scale,
            scrollRect,
            scrollRef,
            showPagePreviews,
        }),
        [currentPage, maxPages, scale, scrollRef, scrollRect, showPagePreviews],
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useCachedPDFContext = (): CachedPDFContext => {
    return useContext(Context);
};
